/**
 * Kunden-Tab Vertragsabgleich: Positionen gegen Systeme legen.
 *
 * Anlass Fortis Unum / WKR 0118 - neue FreePBX-Position und Nextcloud-Entfall
 * waren im Tool unsichtbar, voip.fortis-unum.at lag unter itkfm.
 */

import { useCallback, useMemo, useState } from 'react';
import { FileText } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import type { BadgeTone } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Combobox } from '@/components/ui/Combobox';
import { Dialog } from '@/components/ui/Dialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Input } from '@/components/ui/Input';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import {
  useAcknowledgePositions,
  useArchiveSystem,
  useContractReconciliation,
  useLinkPosition,
  useMoveSystemToCustomer,
  useUnlinkPosition,
} from '@/hooks/useContractReconciliation';
import { formatCurrency } from '@/lib/format';
import type {
  HostingSuggestion,
  LinkedSystem,
  PositionStatus,
  PositionSuggestion,
  ReconciliationPosition,
} from '@/types/contractReconciliation';

interface Props {
  customerId: string;
}

interface LinkTarget {
  position: ReconciliationPosition;
}

interface UnlinkTarget {
  position: ReconciliationPosition;
  system: LinkedSystem;
}

interface MoveTarget {
  systemId: string;
  name: string;
  previousCustomerId: string;
  previousCustomerName: string;
}

interface ArchiveTarget {
  system: LinkedSystem;
}

const STATUS_TONE: Record<PositionStatus, BadgeTone> = {
  neu: 'warning',
  aktiv: 'success',
  entfernt: 'danger',
  quittiert: 'neutral',
};

const STATUS_LABEL: Record<PositionStatus, string> = {
  neu: 'neu',
  aktiv: 'aktiv',
  entfernt: 'entfernt',
  quittiert: 'erledigt',
};

export function PositionStatusBadge({
  status,
}: {
  status: PositionStatus;
}): JSX.Element {
  return (
    <Badge tone={STATUS_TONE[status]} data-testid={`status-${status}`}>
      {STATUS_LABEL[status]}
    </Badge>
  );
}

function positionTitle(pos: ReconciliationPosition): string {
  return pos.product_key ?? '(ohne Produktschlüssel)';
}

function firstLiveSystem(pos: ReconciliationPosition): LinkedSystem | undefined {
  return pos.linked_systems.find((sys) => !sys.archived);
}

function hostHint(fqdn: string | null, hostname: string | null): string | undefined {
  return fqdn ?? hostname ?? undefined;
}

function ohneStrich(text: string): string {
  return text === '–' ? '' : text;
}

function PositionsTable({
  customerId,
  positions,
  acknowledge,
  link,
  onOpenLink,
  onOpenUnlink,
  onOpenArchive,
  onOpenMoveFromSuggestion,
}: {
  customerId: string;
  positions: ReconciliationPosition[];
  acknowledge: ReturnType<typeof useAcknowledgePositions>;
  link: ReturnType<typeof useLinkPosition>;
  onOpenLink: (position: ReconciliationPosition) => void;
  onOpenUnlink: (position: ReconciliationPosition, system: LinkedSystem) => void;
  onOpenArchive: (system: LinkedSystem) => void;
  onOpenMoveFromSuggestion: (sug: PositionSuggestion) => void;
}): JSX.Element {
  const columns = useMemo((): ReadonlyArray<Column<ReconciliationPosition>> => {
    return [
      {
        id: 'position',
        header: 'Position',
        filterValue: (pos) =>
          [positionTitle(pos), pos.notes ?? ''].filter(Boolean).join(' '),
        cell: (pos) => (
          <>
            <div className="font-semibold">{positionTitle(pos)}</div>
            {pos.notes ? <div className="text-xs text-muted">{pos.notes}</div> : null}
          </>
        ),
      },
      {
        id: 'kategorie',
        header: 'Kategorie',
        filter: 'select',
        filterValue: (pos) => pos.service_category,
        cell: (pos) => (
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="primary">{pos.service_category}</Badge>
            {pos.contract_number ? (
              <span className="text-xs text-muted">Vertrag {pos.contract_number}</span>
            ) : null}
          </div>
        ),
      },
      {
        id: 'betrag',
        header: 'Betrag',
        filterValue: (pos) => ohneStrich(formatCurrency(pos.line_total)),
        cell: (pos) => (
          <>
            <div>{formatCurrency(pos.line_total)}</div>
            {pos.quantity != null || pos.unit ? (
              <div className="text-xs text-muted">
                {pos.quantity != null ? pos.quantity : ''}
                {pos.unit ? ` ${pos.unit}` : ''}
              </div>
            ) : null}
          </>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        filter: 'select',
        filterValue: (pos) => STATUS_LABEL[pos.status],
        cell: (pos) => <PositionStatusBadge status={pos.status} />,
      },
      {
        id: 'systeme',
        header: 'Systeme',
        filterValue: (pos) => pos.linked_systems.map((sys) => sys.name).join(' '),
        cell: (pos) =>
          pos.linked_systems.length === 0 ? (
            '–'
          ) : (
            <ul className="flex flex-col gap-1">
              {pos.linked_systems.map((sys) => (
                <li key={sys.link_id} className="flex flex-wrap items-center gap-2">
                  <span>{sys.name}</span>
                  {sys.hosted_by_customer_name ? (
                    <span className="text-xs text-muted">
                      gehostet bei {sys.hosted_by_customer_name}
                    </span>
                  ) : null}
                  {sys.archived ? <Badge tone="neutral">archiviert</Badge> : null}
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Verknüpfung zu ${sys.name} lösen`}
                    onClick={() => onOpenUnlink(pos, sys)}
                  >
                    Verknüpfung lösen
                  </Button>
                </li>
              ))}
            </ul>
          ),
      },
      {
        id: 'aktionen',
        header: 'Aktionen',
        filter: false,
        cell: (pos) => {
          const live = firstLiveSystem(pos);
          return (
            <div className="flex flex-col items-start gap-1">
              <Button variant="secondary" size="sm" onClick={() => onOpenLink(pos)}>
                System verknüpfen
              </Button>
              {pos.status !== 'quittiert' ? (
                <Button
                  variant="ghost"
                  size="sm"
                  loading={
                    acknowledge.isPending &&
                    acknowledge.variables?.serviceProfileIds.includes(pos.id)
                  }
                  onClick={() =>
                    acknowledge.mutate({
                      customerId,
                      serviceProfileIds: [pos.id],
                    })
                  }
                >
                  Als erledigt markieren
                </Button>
              ) : null}
              {pos.status === 'entfernt' && live ? (
                <Button variant="danger" size="sm" onClick={() => onOpenArchive(live)}>
                  System archivieren
                </Button>
              ) : null}
            </div>
          );
        },
      },
    ];
  }, [
    acknowledge,
    customerId,
    onOpenArchive,
    onOpenLink,
    onOpenUnlink,
  ]);

  return (
    <DataTable
      caption="Vertragspositionen"
      columns={columns}
      rows={positions}
      rowKey={(pos) => pos.id}
      storageKey="vertragspositionen"
      rowTestId={(pos) => `position-${pos.id}`}
      rowExtra={(pos) =>
        pos.suggestions.length > 0 ? (
          <div data-testid={`position-${pos.id}-suggestions`}>
            <p className="mb-2 text-sm font-medium">Vorschläge:</p>
            <div className="flex flex-col gap-2">
              {pos.suggestions.map((sug) => (
                <div key={sug.system_id} className="flex flex-wrap items-baseline gap-2">
                  {sug.kind === 'eigenes_system' ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        link.mutate({
                          customerId,
                          serviceProfileId: pos.id,
                          systemId: sug.system_id,
                        })
                      }
                    >
                      {sug.name} verknüpfen
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onOpenMoveFromSuggestion(sug)}
                    >
                      {sug.name}: zum Kunden verschieben (gehostet bei {sug.customer_name})
                    </Button>
                  )}
                  <span className="text-xs text-muted">{sug.reason}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null
      }
    />
  );
}

export function ContractReconciliationTab({ customerId }: Props): JSX.Element {
  const { data, isLoading, isError, error } = useContractReconciliation(customerId);
  const link = useLinkPosition();
  const unlink = useUnlinkPosition();
  const acknowledge = useAcknowledgePositions();
  const move = useMoveSystemToCustomer();
  const archive = useArchiveSystem();

  const [linkTarget, setLinkTarget] = useState<LinkTarget | null>(null);
  const [linkSystemId, setLinkSystemId] = useState<string | null>(null);
  const [linkNote, setLinkNote] = useState('');
  const [unlinkTarget, setUnlinkTarget] = useState<UnlinkTarget | null>(null);
  const [moveTarget, setMoveTarget] = useState<MoveTarget | null>(null);
  const [keepHosted, setKeepHosted] = useState(true);
  const [archiveTarget, setArchiveTarget] = useState<ArchiveTarget | null>(null);

  const openLink = useCallback((position: ReconciliationPosition): void => {
    link.reset();
    setLinkSystemId(null);
    setLinkNote('');
    setLinkTarget({ position });
  }, [link]);

  const openMove = useCallback(
    (
      systemId: string,
      name: string,
      previousCustomerId: string,
      previousCustomerName: string
    ): void => {
      move.reset();
      setKeepHosted(true);
      setMoveTarget({ systemId, name, previousCustomerId, previousCustomerName });
    },
    [move]
  );

  const openMoveFromSuggestion = useCallback(
    (sug: PositionSuggestion): void => {
      openMove(sug.system_id, sug.name, sug.customer_id, sug.customer_name);
    },
    [openMove]
  );

  const openMoveFromHosting = useCallback(
    (sug: HostingSuggestion): void => {
      openMove(
        sug.system_id,
        sug.name,
        sug.current_customer_id,
        sug.current_customer_name
      );
    },
    [openMove]
  );

  const openUnlink = useCallback(
    (position: ReconciliationPosition, system: LinkedSystem): void => {
      unlink.reset();
      setUnlinkTarget({ position, system });
    },
    [unlink]
  );

  const openArchive = useCallback(
    (system: LinkedSystem): void => {
      archive.reset();
      setArchiveTarget({ system });
    },
    [archive]
  );

  if (isLoading) return <SkeletonRows rows={4} cols={4} />;
  if (isError) return <ErrorAlert error={error} />;
  if (!data) return <SkeletonRows rows={4} cols={4} />;

  const positions = data.positions;
  const neuCount = positions.filter((p) => p.status === 'neu').length;
  const entferntCount = positions.filter((p) => p.status === 'entfernt').length;
  const unassignedCount = data.unassigned_systems.length;

  const confirmLink = (): void => {
    if (!linkTarget || !linkSystemId) return;
    link.mutate(
      {
        customerId,
        serviceProfileId: linkTarget.position.id,
        systemId: linkSystemId,
        note: linkNote.trim() === '' ? null : linkNote,
      },
      { onSuccess: () => setLinkTarget(null) }
    );
  };

  const confirmUnlink = (): void => {
    if (!unlinkTarget) return;
    unlink.mutate(
      {
        customerId,
        serviceProfileId: unlinkTarget.position.id,
        systemId: unlinkTarget.system.system_id,
      },
      { onSuccess: () => setUnlinkTarget(null) }
    );
  };

  const confirmMove = (): void => {
    if (!moveTarget) return;
    move.mutate(
      {
        systemId: moveTarget.systemId,
        customerId,
        hostedByCustomerId: keepHosted ? moveTarget.previousCustomerId : null,
        previousCustomerId: moveTarget.previousCustomerId,
      },
      { onSuccess: () => setMoveTarget(null) }
    );
  };

  const confirmArchive = (): void => {
    if (!archiveTarget) return;
    archive.mutate(
      { customerId, systemId: archiveTarget.system.system_id },
      { onSuccess: () => setArchiveTarget(null) }
    );
  };

  const inlineError =
    (!linkTarget && link.isError ? link.error : null) ??
    (acknowledge.isError ? acknowledge.error : null);

  return (
    <div className="flex flex-col gap-4">
      <Card title="Vertragsabgleich">
        <p className="mb-4 text-sm text-muted">
          {positions.length} {positions.length === 1 ? 'Position' : 'Positionen'},{' '}
          {neuCount} neu, {entferntCount} entfernt, {unassignedCount}{' '}
          {unassignedCount === 1 ? 'System' : 'Systeme'} ohne Position
        </p>

        {inlineError ? <ErrorAlert error={inlineError} /> : null}

        {positions.length === 0 ? (
          <EmptyState
            icon={<FileText className="size-8" />}
            title="Keine Vertragspositionen"
            description="Entweder ist kein Vertrag zugeordnet, oder die Positionen tragen keine Kategorie (custom_value2)."
          />
        ) : (
          <PositionsTable
            customerId={customerId}
            positions={positions}
            acknowledge={acknowledge}
            link={link}
            onOpenLink={openLink}
            onOpenUnlink={openUnlink}
            onOpenArchive={openArchive}
            onOpenMoveFromSuggestion={openMoveFromSuggestion}
          />
        )}
      </Card>

      {data.hosting_suggestions.length > 0 ? (
        <Card
          title="Vermutlich gehostete Systeme"
          description="Systeme, deren Name auf diesen Kunden zeigt, die aber unter einem anderen Kunden geführt werden."
        >
          <ul className="flex flex-col gap-3">
            {data.hosting_suggestions.map((sug) => (
              <li
                key={sug.system_id}
                className="flex flex-col gap-1 border-b border-border pb-3 last:border-b-0 last:pb-0"
              >
                <div className="font-medium">{sug.name}</div>
                <div className="text-sm text-muted">
                  {sug.fqdn ?? sug.hostname ?? '–'}
                </div>
                <div className="text-sm">steht unter {sug.current_customer_name}</div>
                <div className="text-sm text-muted">
                  Treffer in {sug.matched_on}: {sug.matched_token}
                </div>
                <div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => openMoveFromHosting(sug)}
                  >
                    Zum Kunden verschieben (gehostet bei {sug.current_customer_name})
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {data.unassigned_systems.length > 0 ? (
        <Card title="Systeme ohne Vertragsposition">
          <ul className="flex flex-col gap-2">
            {data.unassigned_systems.map((sys) => (
              <li key={sys.system_id} className="text-sm">
                <span className="font-medium">{sys.name}</span>
                <span className="ml-2 text-muted">
                  {sys.fqdn ?? sys.hostname ?? '–'}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {linkTarget ? (
        <Dialog
          open
          onClose={() => {
            setLinkTarget(null);
            link.reset();
          }}
          title="System verknüpfen"
          description={`Position ${positionTitle(linkTarget.position)}`}
          footer={
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  setLinkTarget(null);
                  link.reset();
                }}
              >
                Abbrechen
              </Button>
              <Button
                variant="primary"
                disabled={!linkSystemId}
                loading={link.isPending}
                onClick={confirmLink}
              >
                Verknüpfen
              </Button>
            </>
          }
        >
          {link.isError ? <ErrorAlert error={link.error} /> : null}
          <div className="flex flex-col gap-3">
            <Combobox
              label="System"
              value={linkSystemId}
              onChange={setLinkSystemId}
              options={data.unassigned_systems.map((sys) => ({
                value: sys.system_id,
                label: sys.name,
                hint: hostHint(sys.fqdn, sys.hostname),
              }))}
              placeholder="System wählen …"
              emptyText="Keine Systeme ohne Position."
            />
            <Input
              label="Notiz"
              value={linkNote}
              onChange={(event) => setLinkNote(event.target.value)}
            />
          </div>
        </Dialog>
      ) : null}

      {unlinkTarget ? (
        <Dialog
          open
          onClose={() => {
            setUnlinkTarget(null);
            unlink.reset();
          }}
          title="Verknüpfung lösen"
          footer={
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  setUnlinkTarget(null);
                  unlink.reset();
                }}
              >
                Abbrechen
              </Button>
              <Button variant="danger" loading={unlink.isPending} onClick={confirmUnlink}>
                Lösen
              </Button>
            </>
          }
        >
          {unlink.isError ? <ErrorAlert error={unlink.error} /> : null}
          <p>
            Die Verknüpfung zwischen Position {positionTitle(unlinkTarget.position)} und
            System {unlinkTarget.system.name} wird gelöst.
          </p>
        </Dialog>
      ) : null}

      {moveTarget ? (
        <Dialog
          open
          onClose={() => {
            setMoveTarget(null);
            move.reset();
          }}
          title="System zum Kunden verschieben"
          footer={
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  setMoveTarget(null);
                  move.reset();
                }}
              >
                Abbrechen
              </Button>
              <Button variant="primary" loading={move.isPending} onClick={confirmMove}>
                Verschieben
              </Button>
            </>
          }
        >
          {move.isError ? <ErrorAlert error={move.error} /> : null}
          <div className="flex flex-col gap-3">
            {/* Der Satz behauptet den Hoster NICHT - das entscheidet die
                Checkbox darunter, und ein Text, der ihr widerspricht, wäre
                schlimmer als keiner. */}
            <p>
              Das System {moveTarget.name} wechselt den Kunden. Seine Dienste und
              Verbindungsprofile ziehen mit, der Standort wird geleert, und
              Verknüpfungen zu Positionen von „{moveTarget.previousCustomerName}“
              werden gelöst.
            </p>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={keepHosted}
                onChange={(event) => setKeepHosted(event.target.checked)}
                aria-label={`als gehostet bei ${moveTarget.previousCustomerName} eintragen`}
              />
              als gehostet bei {moveTarget.previousCustomerName} eintragen
            </label>
          </div>
        </Dialog>
      ) : null}

      {archiveTarget ? (
        <Dialog
          open
          onClose={() => {
            setArchiveTarget(null);
            archive.reset();
          }}
          title="System archivieren"
          footer={
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  setArchiveTarget(null);
                  archive.reset();
                }}
              >
                Abbrechen
              </Button>
              <Button
                variant="danger"
                loading={archive.isPending}
                onClick={confirmArchive}
              >
                Archivieren
              </Button>
            </>
          }
        >
          {archive.isError ? <ErrorAlert error={archive.error} /> : null}
          <p>
            System {archiveTarget.system.name} wird archiviert und fällt damit aus der
            Wartung (Soft-Delete, umkehrbar auf der Systemseite).
          </p>
        </Dialog>
      ) : null}
    </div>
  );
}
